import {
  LinkType,
  ListBlock,
  ListTransformerProps,
  RestaurantTagFilterer,
  SimplifyGatsbyQuery,
  useParkFoodWaitTimes,
  mapCustomerDisplayApiToCustomerDisplayWeb,
  useIsParkOpenNow,
} from "@parksandresorts/core";
import { RestaurantGroup } from "@parksandresorts/core/dist/components/elements/list-block/restaurant-list-renderer/restaurant-list-renderer.js";
import { useCallback, useMemo } from "react";
import SiteConfigurationStore from "components/stores/site-configuration-store";

type ListObjectExtractor<ListObjectType, TypeName> = ListObjectType extends { __typename: TypeName }
  ? ListObjectType
  : never;

type SomeListObject = SimplifyGatsbyQuery<Queries.ListBlockFragment>["lists"][0]["listObjects"][0];
type RestaurantListObject = ListObjectExtractor<SomeListObject, "ContentfulRestaurantBlock">;
type GeneralContentListObject = ListObjectExtractor<SomeListObject, "ContentfulGeneralContentBlock">;

type SomeFilterGroupFilterObject =
  SimplifyGatsbyQuery<Queries.ListBlockFragment>["filterGroupFilters"][0]["listObjects"][0];
type TagListObject = ListObjectExtractor<SomeFilterGroupFilterObject, "ContentfulTag">;

type ValidRestaurantListItem = RestaurantListObject | GeneralContentListObject;

function isRestaurantOrGeneralContent(val: SomeListObject): val is ValidRestaurantListItem {
  return val.__typename === "ContentfulRestaurantBlock" || val.__typename === "ContentfulGeneralContentBlock";
}

function isRestaurantContent(val: ValidRestaurantListItem): val is RestaurantListObject {
  return val.__typename === "ContentfulRestaurantBlock";
}

function isTagObject(val: SomeFilterGroupFilterObject): val is TagListObject {
  return val.__typename === "ContentfulTag";
}

function isDefined<T>(val: T | undefined | null): val is T {
  return val !== undefined && val !== null;
}

type Props = {
  lists: SimplifyGatsbyQuery<Queries.ListBlockFragment>["lists"];
  quickFilters: SimplifyGatsbyQuery<Queries.ListBlockFragment>["filters"];
  filterGroups: SimplifyGatsbyQuery<Queries.ListBlockFragment>["filterGroupFilters"];
  filterHeadline: string;
  filterParagraph: { raw: string };
  displayRestaurantWaitingOrders: boolean;
  restaurantsLinksToPej: boolean;
};

type CustomRestaurantGroup = RestaurantGroup<{ tags: Array<{ id: string; title: string }> }>;

/**
 * This is used to render the ListBlock with RestaurantTagFilterer (search box + custom filters).
 * This will convert the items to the correct format for the RestaurantListItem in core.
 *
 * Note: This is not the only place that converts items to fit into RestaurantListItems. It is currently also done in restaurant-list-item.tsx.
 * This is because that is used when rendering the list items in a different context (not inside a ListBlock) e.g. for /mat-online with sorting options.
 */
export default function RestaurantList(props: Props) {
  if (props.displayRestaurantWaitingOrders) {
    return <WithWaitTimes {...props} />;
  } else {
    return <WithoutWaitTimes {...props} />;
  }
}

const WithWaitTimes = (props: Props) => {
  const foodTerminalIds = useMemo(
    () =>
      props.lists
        .filter(isDefined)
        .flatMap(
          (list) =>
            list.listObjects
              ?.filter(isRestaurantOrGeneralContent)
              .filter(isRestaurantContent)
              .map((res) => res.foodTerminalId),
        )
        .filter(isDefined),
    [props.lists],
  );

  const waitTimes = useParkFoodWaitTimes(foodTerminalIds, 30_000);

  return <Base {...props} waitTimes={waitTimes} />;
};

const WithoutWaitTimes = (props: Props) => {
  return <Base {...props} waitTimes={undefined} />;
};

type BaseProps = Omit<Props, "displayRestaurantWaitingOrders"> & {
  waitTimes: ReturnType<typeof useParkFoodWaitTimes> | undefined;
};
const Base = ({
  lists: originalRestaurantLists,
  quickFilters,
  filterGroups,
  filterHeadline,
  filterParagraph,
  waitTimes,
  /** There are certain features we only want present if we are on a "mat-online" page.
   * we currently use the "restaurantsLinksToPej" as a proxy for being on a "mat-online" page.
   */
  restaurantsLinksToPej: isContextParkfood,
}: BaseProps) => {
  const getLinkDataForRestaurantItem = useCallback(
    (item: RestaurantListObject) => {
      if (!item || (!item.externalPejLink && !item.pageLink)) {
        return null;
      }
      if (item.externalPejLink && isContextParkfood) {
        return {
          linkUrl: item.externalPejLink,
          isExternalLink: true,
        };
      }
      if (item.pageLink && !isContextParkfood) {
        return {
          linkUrl: item.pageLink.slug,
          isExternalLink: false,
        };
      }
      return null;
    },
    [isContextParkfood],
  );

  const isParkOpenNow = useIsParkOpenNow();

  const customerDisplays = useMemo(() => waitTimes?.map(mapCustomerDisplayApiToCustomerDisplayWeb), [waitTimes]);

  const mappedRestaurantLists: CustomRestaurantGroup[] = useMemo(
    () =>
      originalRestaurantLists.filter(isDefined).map((list) => {
        const objects = list.listObjects?.filter(isRestaurantOrGeneralContent).map((restaurantOrGeneralContent) => {
          if (isRestaurantContent(restaurantOrGeneralContent)) {
            const restaurant = restaurantOrGeneralContent;
            const customerDisplayForThisRestaurant = customerDisplays?.find(
              (display) => display.terminalId === restaurant.foodTerminalId,
            );

            const linkData = getLinkDataForRestaurantItem(restaurantOrGeneralContent);

            return {
              type: "restaurant",
              id: restaurant.id,
              tags: restaurant.tags,
              image: restaurant.imageInList?.gatsbyImageData,
              imageAlt: restaurant.imageInList?.description,
              title: restaurant.title ?? "",
              bodyText: restaurant.shortDescriptionInList ?? "",
              linkUrl: linkData?.linkUrl,
              linkType: linkData?.isExternalLink ? LinkType.EXTERNAL : LinkType.INTERNAL,
              foodTerminalId: restaurant.foodTerminalId,
              shouldShowWarningModal: customerDisplayForThisRestaurant?.shouldWarnForLongQueue ?? false,
              shouldShowBlockingModal: customerDisplayForThisRestaurant?.shouldStopWebOrders ?? false,
              isParkOpenNow,
              labels: {
                labelUnderImage: isContextParkfood ? restaurant.label : undefined,
                readMore: SiteConfigurationStore.getLabel("READMORE"),
                waitTimeLabelPrefix: SiteConfigurationStore.getLabel("WAIT_TIME_CURRENT"),
                waitTimeFullQueue: SiteConfigurationStore.getLabel("WAIT_TIME_FULL_QUEUE_WARNING"),
                waitTimeLabel: customerDisplayForThisRestaurant?.waitTimeLabelWeb ?? undefined,
                waitTimeModalHeaderFullQueue: SiteConfigurationStore.getLabel("WAIT_TIME_MODAL_HEADER_FULL_QUEUE"),
                waitTimeModalHeaderLongQueue: SiteConfigurationStore.getLabel("WAIT_TIME_MODAL_HEADER_LONG_QUEUE"),
                waitTimePaused: SiteConfigurationStore.getLabel("WAIT_TIME_LEVEL_PAUSED"),
                goBackButtonLabel: SiteConfigurationStore.getLabel("WAIT_TIME_MODAL_BUTTON_BACK"),
                continueButtonLabel: SiteConfigurationStore.getLabel("WAIT_TIME_MODAL_BUTTON_CONTINUE"),
              },
            } satisfies CustomRestaurantGroup["listObjects"][0];
          } else {
            //GeneralContentListObject will be converted to "ad list items"
            const generalContent = restaurantOrGeneralContent;

            return {
              type: "ad",
              id: generalContent.id,
              tags: generalContent.tags,
              title: generalContent.title,
              preamble: generalContent.preamble?.preamble,
              shortDescriptionInList: generalContent.shortDescriptionInList,
              callToActionButton: {
                title: generalContent.adLink?.title ?? "",
                externalUrl: generalContent.adLink?.externalUrl,
                internalLink: {
                  slug: generalContent.adLink?.internalLink.slug,
                  title: generalContent.adLink?.title ?? "",
                },
              },
            } satisfies CustomRestaurantGroup["listObjects"][0];
          }
        });

        return {
          id: list.id ?? "unknown",
          headline: list.headline ?? "",
          paragraph: list.ingress ?? undefined,
          isExpanded: true,
          align: "start",
          listObjects: objects,
        } satisfies CustomRestaurantGroup;
      }),
    [originalRestaurantLists, customerDisplays, getLinkDataForRestaurantItem, isParkOpenNow, isContextParkfood],
  );

  const filterTagGroups = useMemo(
    () =>
      filterGroups
        .filter(isDefined)
        .map((group) => {
          const tags = group.listObjects.filter(isTagObject).map((tag) => tag);
          return {
            id: group.id,
            title: group.headline,
            tags,
          };
        })
        .filter((tagGroup) => tagGroup.tags.length > 0),

    [filterGroups],
  );

  const restaurantBlockIds = useMemo(
    () =>
      originalRestaurantLists.filter(isDefined).flatMap((list) => {
        return list.listObjects
          ?.filter(isRestaurantOrGeneralContent)
          .filter(isRestaurantContent)
          .map((restaurant) => restaurant.id);
      }),
    [originalRestaurantLists],
  );

  const renderFilter = useCallback(
    (props: ListTransformerProps<CustomRestaurantGroup>) => (
      <RestaurantTagFilterer
        blockIds={restaurantBlockIds}
        filterTagGroups={filterTagGroups}
        quickFilters={quickFilters}
        filterHeadline={filterHeadline}
        filterParagraph={filterParagraph}
        {...props}
      />
    ),
    [filterHeadline, filterParagraph, filterTagGroups, quickFilters, restaurantBlockIds],
  );

  return (
    <div className="block--max-width">
      <ListBlock renderer="RestaurantGroups" data={mappedRestaurantLists} filter={renderFilter} />
    </div>
  );
};
